import { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useForm } from "../../context/FormContext";
import { mvpApi } from "../../../services/api";
import {
  ALL_DIAL_CODES,
  PRIORITY_CODES_SET,
  PRIORITY_DIAL_CODES,
} from "../../../constants/phoneDialCodes";

interface DialCode {
  code: string;
  name: string;
  flag?: string;
}

/** Same as Login.js: map regional-indicator emoji → ISO 3166-1 alpha-2 for flag-icons. */
function flagToIso2(flag: string | undefined): string {
  const parts = [...String(flag || "")];
  if (parts.length < 2) return "";
  const base = 0x1f1e6;
  const a = parts[0].codePointAt(0);
  const b = parts[1].codePointAt(0);
  if (!a || !b) return "";
  if (a < base || a > base + 25 || b < base || b > base + 25) return "";
  return String.fromCharCode(a - base + 65, b - base + 65).toLowerCase();
}

function getCountryIso2(item: DialCode | null | undefined): string {
  const fromFlag = flagToIso2(item?.flag);
  if (fromFlag) return fromFlag;
  if (item?.code === "+1") return "us";
  return "un";
}

const FLAG_BOX_STYLE = { width: "1.1rem", height: "0.82rem" } as const;

function profilePhoneApiError(err: unknown): { status?: number; detail?: string } {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const raw = res?.data?.detail;
  let detail: string | undefined;
  if (typeof raw === "string") detail = raw;
  else if (Array.isArray(raw) && raw[0] && typeof (raw[0] as { msg?: string }).msg === "string") {
    detail = (raw[0] as { msg: string }).msg;
  }
  return { status: res?.status, detail };
}

function isPhoneAlreadyRegisteredError(status: number | undefined, detail: string | undefined): boolean {
  if (status === 409) return true;
  const d = (detail || "").toLowerCase();
  return d.includes("already registered") || d.includes("ya está registrado");
}

function toE164(phoneCountryCode: string, phoneRaw: string): string {
  const raw = (phoneRaw || "").trim();
  if (!raw) return "";
  const compact = raw.replace(/[^\d+]/g, "");
  if (compact.startsWith("+")) return compact;
  const cc = (phoneCountryCode || "").replace(/[^\d+]/g, "") || "+34";
  return `${cc}${compact}`.replace(/\s+/g, "");
}

function nationalFromE164(e164: string, dial: string): string {
  const d = dial.replace(/\s+/g, "");
  if (e164.startsWith(d)) return e164.slice(d.length).trim();
  return e164.replace(/^\+\d{1,4}/, "").trim() || e164;
}

/**
 * Login-style dial + number + profile OTP when the representative enters a new
 * mobile after "Move to applicant section".
 */
export default function RepresentativePhoneOtpField() {
  const { t } = useLang();
  const { data, update } = useForm();
  const [showDial, setShowDial] = useState(false);
  const [dialSearch, setDialSearch] = useState("");
  const dialRef = useRef<HTMLDivElement>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const code = (data.phoneCountryCode as string) ?? "+34";
  const number = (data.phone as string) ?? "";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dialRef.current && !dialRef.current.contains(e.target as Node)) {
        setShowDial(false);
        setDialSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const filtered = dialSearch
    ? ALL_DIAL_CODES.filter(
        (c) =>
          c.code.includes(dialSearch) ||
          c.name.toLowerCase().includes(dialSearch.toLowerCase())
      )
    : ALL_DIAL_CODES.filter(
        (c) => !PRIORITY_CODES_SET.has(c.code + c.name.split(" /")[0])
      );

  const currentFlag = useMemo((): DialCode | null => {
    const found =
      PRIORITY_DIAL_CODES.find((c) => c.code === code) ||
      ALL_DIAL_CODES.find((c) => c.code === code);
    return found ?? null;
  }, [code]);

  const handleSelectCode = (c: DialCode) => {
    update("phoneCountryCode", c.code);
    setShowDial(false);
    setDialSearch("");
    update("repPhoneOtpVerified", false);
    setOtpSent(false);
    setOtpCode("");
  };

  const e164 = toE164(code, number);

  const handleRequestOtp = async () => {
    setError(null);
    if (!e164 || e164.length < 10) {
      setError(t("validation.invalidPhone"));
      return;
    }
    setLoading(true);
    try {
      await mvpApi.requestProfilePhoneOtp(e164, "sms");
      setOtpSent(true);
      setCooldown(120);
      update("repPhoneOtpVerified", false);
    } catch (err: unknown) {
      const { status, detail } = profilePhoneApiError(err);
      if (isPhoneAlreadyRegisteredError(status, detail)) {
        setError(t("section1.phoneAlreadyRegistered"));
        return;
      }
      setError(detail && typeof detail === "string" ? detail : t("section1.phoneOtpSendError"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError(null);
    const digits = otpCode.replace(/\D/g, "").trim();
    if (!e164 || digits.length !== 6) {
      setError(t("section1.phoneOtpSixDigitsHint"));
      return;
    }
    setLoading(true);
    try {
      const resp = await mvpApi.verifyProfilePhoneOtp(e164, digits);
      const user = resp?.data?.user;
      const verified = String(user?.phone ?? e164).trim();
      if (verified) {
        update("phone", nationalFromE164(verified, code));
      }
      update("repPhoneOtpVerified", true);
      setOtpSent(false);
      setOtpCode("");
    } catch (err: unknown) {
      const { status, detail } = profilePhoneApiError(err);
      if (isPhoneAlreadyRegisteredError(status, detail)) {
        setError(t("section1.phoneAlreadyRegistered"));
        return;
      }
      setError(detail && typeof detail === "string" ? detail : t("validation.invalidPhone"));
    } finally {
      setLoading(false);
    }
  };

  const onNumberChange = (v: string) => {
    update("phone", v);
    update("repPhoneOtpVerified", false);
    setOtpSent(false);
    setOtpCode("");
  };

  return (
    <div className="py-3 border-b border-slate-50 last:border-0">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="text-[12px] font-semibold text-slate-400 uppercase tracking-wide">
          {t("section1.phone")}
        </label>
        {data.repPhoneOtpVerified && (
          <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
            {t("section1.phoneVerifiedBadge")}
          </span>
        )}
      </div>
      <div ref={dialRef} className="relative w-full min-w-0">
        <div
          className={`flex h-12 w-full min-w-0 bg-white border rounded-xl transition-all ${
            showDial
              ? "border-emerald-400 ring-2 ring-emerald-300"
              : number
                ? "border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-300"
                : "border-slate-200 focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-300"
          }`}
          style={{ overflow: "visible" }}
        >
          <button
            type="button"
            onClick={() => setShowDial((v) => !v)}
            className="h-full pl-3 pr-2.5 flex items-center gap-2 border-r border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap shrink-0 rounded-l-xl"
          >
            <span
              className={`fi fi-${getCountryIso2(currentFlag)} rounded-sm shrink-0`}
              style={FLAG_BOX_STYLE}
              aria-hidden
            />
            <span className="text-[13px] font-semibold text-slate-700">{code}</span>
            <i
              className={`ri-arrow-${showDial ? "up" : "down"}-s-line text-slate-400 text-xs`}
            />
          </button>
          <input
            type="tel"
            inputMode="numeric"
            value={number}
            onChange={(e) => onNumberChange(e.target.value)}
            placeholder={t("section1.phonePlaceholder")}
            style={{ fontSize: "16px" }}
            className="min-w-0 flex-1 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent"
          />
          <button
            type="button"
            onClick={handleRequestOtp}
            disabled={loading || cooldown > 0 || !e164 || e164.length < 10}
            className="h-full shrink-0 px-2.5 sm:px-3 border-l border-slate-200 rounded-r-xl text-[12px] sm:text-[13px] font-semibold bg-slate-50 text-slate-800 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap"
          >
            {cooldown > 0 ? `${t("section1.sendPhoneOtp")} (${cooldown}s)` : t("section1.sendPhoneOtp")}
          </button>
        </div>
        {showDial && (
          <div
            className="absolute top-[calc(100%+4px)] left-0 w-72 bg-white border border-slate-200 rounded-xl z-50 overflow-hidden"
            style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}
          >
            <div className="p-2 border-b border-slate-100">
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                <i className="ri-search-line text-slate-400 text-sm" />
                <input
                  type="text"
                  value={dialSearch}
                  onChange={(e) => setDialSearch(e.target.value)}
                  placeholder="Buscar país o código…"
                  autoFocus
                  style={{ fontSize: "16px", touchAction: "manipulation" }}
                  className="flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none"
                />
                {dialSearch && (
                  <button type="button" onClick={() => setDialSearch("")} className="cursor-pointer">
                    <i className="ri-close-line text-slate-400 text-sm" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto">
              {!dialSearch && (
                <>
                  {PRIORITY_DIAL_CODES.map((c) => (
                    <button
                      key={`p-${c.code}-${c.name}`}
                      type="button"
                      onClick={() => handleSelectCode(c)}
                      className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${
                        c.code === code ? "bg-slate-100" : "hover:bg-slate-50"
                      }`}
                    >
                      <span
                        className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                        style={FLAG_BOX_STYLE}
                        aria-hidden
                      />
                      <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                      <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                    </button>
                  ))}
                  <div className="border-t border-slate-100 my-1" />
                </>
              )}
              {filtered.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-400 text-center">Sin resultados</div>
              ) : (
                filtered.map((c) => (
                  <button
                    key={`a-${c.code}-${c.name}`}
                    type="button"
                    onClick={() => handleSelectCode(c)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${
                      c.code === code ? "bg-slate-100" : "hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                      style={FLAG_BOX_STYLE}
                      aria-hidden
                    />
                    <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                    <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-2">
        {otpSent && !data.repPhoneOtpVerified && (
          <div className="flex flex-row items-stretch gap-2 w-full min-w-0">
            <label className="flex-1 min-w-0 block">
              <span className="sr-only">{t("section1.otpCodePlaceholder")}</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder={t("section1.otpCodePlaceholder")}
                style={{ fontSize: "16px", colorScheme: "light" }}
                className="w-full h-12 px-2 rounded-xl border border-slate-200 bg-white text-slate-900 text-center font-mono tracking-[0.25em] placeholder:text-slate-400 placeholder:tracking-normal shadow-none focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
              />
            </label>
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={loading}
              className="shrink-0 h-12 px-4 rounded-xl text-[13px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer transition-colors whitespace-nowrap"
            >
              {t("section1.verifyPhoneOtp")}
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-[12px] text-red-600 mt-2">{error}</p>}
      <p className="text-[11px] text-slate-400 mt-1.5">{t("section1.phoneOtpHelper")}</p>
    </div>
  );
}
